import {
  Flex,
  Card,
  Text,
  Button,
  AchIcon,
  CreditCardIcon,
  AmazonPayIcon,
  AlipayIcon,
  CashappIcon,
  Notice,
  LinkStripeIcon,
  Separator,
  Heading,
  ExternalLinkIcon,
} from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { type FragmentOf, graphql, readFragment } from '~publish/graphql'
import { capitalize } from '~publish/helpers/capitalize'

import {
  useGetPortalSession,
  PortalOptions,
} from '../hooks/useGetPortalSession'
import styles from '../Settings.module.css'
import { getSettingsUrl } from '../util/getSettingsUrl'

import detailsStyles from './PaymentDetails.module.css'

export const PaymentDetails_Billing = graphql(/* GraphQL */ `
  fragment PaymentDetails_Billing on Billing {
    gateway {
      gatewayId
    }
    paymentDetails {
      hasPaymentDetails
      creditCard {
        brand
        last4
        expMonth
        expYear
      }
      paymentMethod
    }
    ... on OBBilling {
      subscription {
        isPaymentPastDue
        plan {
          id
        }
        trial {
          isActive
        }
      }
    }
  }
`)

export const PaymentDetails_Organization = graphql(/* GraphQL */ `
  fragment PaymentDetails_Organization on AccountOrganization {
    dismissedObjects {
      id
    }
  }
`)

const paymentMethodNames = new Map<string, string>([
  ['bank', 'Bank Account'],
  ['link', 'Link'],
  ['amazonPay', 'Amazon Pay'],
  ['alipay', 'Alipay'],
  ['cashApp', 'Cash App'],
])

const formatPaymentMethodName = (paymentMethod: string): string => {
  return paymentMethodNames.get(paymentMethod) ?? capitalize(paymentMethod)
}

const PaymentMethodIcon = ({
  paymentMethod,
}: {
  paymentMethod: string | null
}): JSX.Element => {
  if (paymentMethod === 'bank') {
    return <AchIcon size="medium" />
  }
  if (paymentMethod === 'link') {
    return <LinkStripeIcon size="medium" />
  }
  if (paymentMethod === 'amazonPay') {
    return <AmazonPayIcon size="medium" />
  }
  if (paymentMethod === 'alipay') {
    return <AlipayIcon size="medium" />
  }
  if (paymentMethod === 'cashApp') {
    return <CashappIcon size="medium" />
  }
  return <CreditCardIcon size="medium" />
}

const CreditCardDetails = ({
  brand,
  last4,
  expMonth,
  expYear,
}: {
  brand?: string
  last4?: string
  expMonth?: string
  expYear?: string
}): JSX.Element => {
  // TODO: can we make these non-nullable in the schema?
  if (!brand || !last4 || !expMonth || !expYear) {
    return <Text>Card</Text>
  }
  return (
    <>
      <Flex gap="xs" direction="row" align="center">
        <PaymentMethodIcon paymentMethod={'card'} />
        <Text>
          {capitalize(brand)}{' '}
          <span className={detailsStyles.creditCardLast4} aria-hidden>
            <span>••••</span> <span>••••</span> <span>••••</span>
          </span>{' '}
          {last4}
        </Text>
      </Flex>
      <Flex gap="xs" direction="row" align="center">
        <Text>
          Expires {expMonth}/{expYear}
        </Text>
      </Flex>
    </>
  )
}

/**
 * Checks if the payment past due banner has been dismissed
 * @param organization
 * @returns
 */
const hasCompletedPaymentUpdate = (
  organization: FragmentOf<typeof PaymentDetails_Organization> | null,
): boolean => {
  if (!organization) return false
  const orgData = readFragment(PaymentDetails_Organization, organization)
  return (
    orgData.dismissedObjects?.some(
      (object) => object?.id === 'payment-past-due-banner',
    ) ?? false
  )
}

/**
 * Shows the payment past due state if the payment is past due and the banner has not been dismissed
 * Only applies to New Buffer organizations
 * @param data
 * @param organization
 * @returns
 */
const showPaymentPastDueState = (
  data: FragmentOf<typeof PaymentDetails_Billing> | null,
  organization: FragmentOf<typeof PaymentDetails_Organization> | null,
): boolean => {
  if (!data) return false

  const billingData = readFragment(PaymentDetails_Billing, data)
  const isPastDue =
    billingData.__typename === 'OBBilling' &&
    Boolean(billingData.subscription?.isPaymentPastDue)
  const hasCompletedUpdate = hasCompletedPaymentUpdate(organization)

  return isPastDue && !hasCompletedUpdate
}

/**
 * Checks if the user is on a paid plan (not free and not on trial)
 * Only applies to OBBilling (OneBuffer) customers
 * @param data
 * @returns
 */
const isOnPaidPlan = (
  data: FragmentOf<typeof PaymentDetails_Billing> | null,
): boolean => {
  if (!data) return false

  const billingData = readFragment(PaymentDetails_Billing, data)

  if (billingData.__typename === 'OBBilling') {
    const planId = billingData.subscription?.plan?.id
    const isOnTrial = billingData.subscription?.trial?.isActive
    return planId !== undefined && planId !== 'free' && !isOnTrial
  }

  return false
}

export const PaymentDetails = ({
  canAddPaymentDetails,
  data,
  organizationData,
}: {
  canAddPaymentDetails: boolean
  data?: FragmentOf<typeof PaymentDetails_Billing> | null
  organizationData?: FragmentOf<typeof PaymentDetails_Organization> | null
}): JSX.Element | null => {
  const organizationId = useOrganizationId()
  const billingData = data ? readFragment(PaymentDetails_Billing, data) : null
  const paymentDetails = billingData?.paymentDetails
  const paymentPastDue = data
    ? showPaymentPastDueState(data, organizationData ?? null)
    : false

  const { getCustomerPortalSession, loading: portalSessionLoading } =
    useGetPortalSession({
      portalOption: PortalOptions.MAIN_PAGE,
    })

  const trackPortalSession = ({ ctaButton }: { ctaButton: string }): void => {
    BufferTracker.billingPortalSessionCreated({
      organizationId: organizationId ?? null,
      customerId: billingData?.gateway?.gatewayId ?? null,
      ctaApp: 'publish',
      ctaView: 'billing',
      ctaLocation: 'paymentDetails',
      ctaButton,
      ctaVersion: '1',
    })
  }

  const isCreditCard = paymentDetails && !!paymentDetails.creditCard
  const showPaymentMethod = paymentDetails && paymentDetails.hasPaymentDetails
  const isUserOnPaidPlan = isOnPaidPlan(data ?? null)

  if (!paymentDetails || !paymentDetails.hasPaymentDetails) {
    // Show component for edge case: user is on paid plan but has no payment details
    if (!isUserOnPaidPlan) {
      return null
    }
  }

  const isPaidPlanWithoutPaymentDetails = isUserOnPaidPlan && !showPaymentMethod

  return (
    <>
      <Separator className={styles.fullWidth} />
      {paymentPastDue && (
        <Notice variant="warning">
          <Text>
            Your payment is past due. Please update your payment method to
            continue using Buffer.
          </Text>
        </Notice>
      )}
      <Flex gap="xs" direction="row" align="center" justify="between" fullWidth>
        <Heading size="medium">Payment Method</Heading>
        <Button
          size="medium"
          variant="secondary"
          onClick={(): void => {
            trackPortalSession({
              ctaButton: `${showPaymentMethod || isPaidPlanWithoutPaymentDetails ? 'updateMethod' : 'addMethod'}`,
            })
            getCustomerPortalSession(getSettingsUrl())
          }}
          disabled={portalSessionLoading || !canAddPaymentDetails}
        >
          {showPaymentMethod || isPaidPlanWithoutPaymentDetails ? 'Update Method' : 'Add Method'}
          <ExternalLinkIcon />
        </Button>
      </Flex>
      <Card className={styles.fullWidth}>
        <Flex
          gap="xs"
          direction="row"
          align="center"
          justify="between"
          fullWidth
        >
          {isPaidPlanWithoutPaymentDetails ? (
            <Flex gap="xs" direction="row" align="center">
              <PaymentMethodIcon paymentMethod={null} />
              <Text>Card</Text>
            </Flex>
          ) : isCreditCard ? (
            <CreditCardDetails
              brand={paymentDetails?.creditCard?.brand}
              last4={paymentDetails?.creditCard?.last4}
              expMonth={paymentDetails?.creditCard?.expMonth}
              expYear={paymentDetails?.creditCard?.expYear}
            />
          ) : (
            <Flex gap="xs" direction="row" align="center">
              <PaymentMethodIcon
                paymentMethod={paymentDetails?.paymentMethod ?? null}
              />
              <Text>
                {/* TODO: can we make this non-nullable in the schema? */}
                {paymentDetails?.paymentMethod &&
                  formatPaymentMethodName(paymentDetails.paymentMethod)}
              </Text>
            </Flex>
          )}
        </Flex>
      </Card>
    </>
  )
}

export default PaymentDetails
